import { useEffect, useRef } from "react";

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  staggerDelay?: number;
  childSelector?: string;
}

export const useScrollReveal = ({
  threshold = 0.15,
  rootMargin = "0px",
  staggerDelay = 180,
  childSelector = "[data-scroll-item]",
}: UseScrollRevealOptions = {}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const children = container.querySelectorAll(childSelector);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add staggered animation to children
            children.forEach((child, index) => {
              const element = child as HTMLElement;
              element.style.transitionDelay = `${index * staggerDelay}ms`;
              element.classList.add("scroll-visible");
            });
            // Unobserve after triggering (animate once)
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, [threshold, rootMargin, staggerDelay, childSelector]);

  return containerRef;
};
